import { CheckCircle2, CircleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import {
  evaluateChoice,
  type ChoiceQuestion,
  type ChoiceResult,
  type FormulaSegment,
  type StructureDiagram,
} from './polymersBiomolecules';
import { chipClass } from './polymersStyles';

/** Formula text with each new link underlined and tinted, so colour is never the only cue. */
export function SegmentFormula({
  segments,
  className = '',
}: {
  segments: FormulaSegment[];
  className?: string;
}) {
  return (
    <span className={`break-words font-mono font-extrabold text-ink ${className}`}>
      {segments.map((segment, index) =>
        segment.link ? (
          <span
            key={`${segment.text}-${index}`}
            className="rounded bg-wash-coral px-0.5 underline decoration-brand-coral decoration-[3px] underline-offset-4"
          >
            {segment.text}
            <span className="sr-only"> (new link)</span>
          </span>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        ),
      )}
    </span>
  );
}

const LABEL_TRIM = 13;
const DOUBLE_BOND_GAP = 4;

/** Draws a structure from model coordinates. Only lines, brackets and text; no artwork. */
export function StructureDiagramView({
  diagram,
  caption,
}: {
  diagram: StructureDiagram;
  caption: string;
}) {
  const atomById = new Map(diagram.atoms.map((atom) => [atom.id, atom]));
  return (
    <figure className="min-w-0">
      <svg
        viewBox={`0 0 ${diagram.width} ${diagram.height}`}
        className="mx-auto block h-auto w-full max-w-[320px]"
        role="img"
        aria-label={diagram.description}
      >
        {diagram.bonds.map((bond) => {
          const from = atomById.get(bond.from)!;
          const to = atomById.get(bond.to)!;
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const length = Math.hypot(dx, dy);
          const ux = dx / length;
          const uy = dy / length;
          const startTrim = from.label ? LABEL_TRIM : 0;
          const endTrim = to.label ? LABEL_TRIM : 0;
          const x1 = from.x + ux * startTrim;
          const y1 = from.y + uy * startTrim;
          const x2 = to.x - ux * endTrim;
          const y2 = to.y - uy * endTrim;
          const offsets = bond.order === 2 ? [-DOUBLE_BOND_GAP, DOUBLE_BOND_GAP] : [0];
          return offsets.map((offset) => (
            <line
              key={`${bond.from}-${bond.to}-${offset}`}
              x1={x1 - uy * offset}
              y1={y1 + ux * offset}
              x2={x2 - uy * offset}
              y2={y2 + ux * offset}
              className={bond.highlight ? 'stroke-brand-coral' : 'stroke-ink'}
              strokeWidth={bond.highlight ? 3.5 : 2.5}
              strokeLinecap="round"
              strokeDasharray={bond.open ? '5 4' : undefined}
            />
          ));
        })}
        {diagram.atoms
          .filter((atom) => atom.label)
          .map((atom) => (
            <text
              key={atom.id}
              x={atom.x}
              y={atom.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-ink font-mono text-[17px] font-extrabold"
            >
              {atom.label}
            </text>
          ))}
        {diagram.brackets ? (
          <>
            <path
              d={`M ${diagram.brackets.left + 8} ${diagram.brackets.top} H ${diagram.brackets.left} V ${diagram.brackets.bottom} H ${diagram.brackets.left + 8}`}
              className="fill-none stroke-ink"
              strokeWidth={2.5}
            />
            <path
              d={`M ${diagram.brackets.right - 8} ${diagram.brackets.top} H ${diagram.brackets.right} V ${diagram.brackets.bottom} H ${diagram.brackets.right - 8}`}
              className="fill-none stroke-ink"
              strokeWidth={2.5}
            />
            <text
              x={diagram.brackets.right + 6}
              y={diagram.brackets.bottom}
              className="fill-ink font-mono text-[15px] font-extrabold italic"
            >
              n
            </text>
          </>
        ) : null}
      </svg>
      <figcaption className="mt-2 text-center text-xs font-bold text-slate2">{caption}</figcaption>
    </figure>
  );
}

export function FeedbackBox({
  correct,
  title,
  children,
}: {
  correct: boolean;
  title?: string;
  children: ReactNode;
}) {
  const Icon = correct ? CheckCircle2 : CircleAlert;
  return (
    <div
      role="status"
      className={`rounded-2xl border p-4 ${
        correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/40 bg-wash-coral'
      }`}
    >
      <p className="flex items-center gap-2 font-extrabold text-ink">
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {title ?? (correct ? 'Correct' : 'Not yet')}
      </p>
      <div className="mt-2 text-sm font-medium leading-6 text-ink-soft">{children}</div>
    </div>
  );
}

/** Predict-then-check multiple choice. Key it by question id so state resets per question. */
export function ChoicePanel({
  question,
  onChecked,
  monoOptions = false,
}: {
  question: ChoiceQuestion;
  onChecked?: (result: ChoiceResult) => void;
  /** Render option labels as formulae. */
  monoOptions?: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<ChoiceResult | null>(null);

  return (
    <div>
      <p className="font-extrabold leading-7 text-ink">{question.prompt}</p>
      <div className="mt-4 grid gap-2" role="group" aria-label="Answer options">
        {question.options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected === option.id}
            onClick={() => {
              setSelected(option.id);
              setResult(null);
            }}
            className={`${chipClass(selected === option.id)} break-words ${monoOptions ? 'font-mono' : ''}`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={selected === null}
        onClick={() => {
          if (selected === null) return;
          const next = evaluateChoice(question, selected);
          setResult(next);
          onChecked?.(next);
        }}
        className="btn-pill-primary mt-4 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Check answer
      </button>
      {result ? (
        <div className="mt-4">
          <FeedbackBox correct={result.status === 'correct'}>{result.message}</FeedbackBox>
        </div>
      ) : null}
    </div>
  );
}
